import React, { useCallback, useEffect, useRef, useState } from 'react';
import { HandCoins, BellRing, Truck } from 'lucide-react';
import SectionShell from '../SectionShell';
import Reveal from '../Reveal';
import { useT } from '../../i18n/LanguageContext';

/**
 * Folie 21 — Deine Möglichkeiten (nach Ablauf des Solution Plans).
 *
 * Interaction: The three option cards are hidden on entry. Each click
 * anywhere inside the slide (except real interactive elements) reveals
 * the next card. Once all three cards are shown, the next click hands
 * control back to SmoothScroll so the presentation advances normally.
 */
export default function SectionMoeglichkeitenNach() {
  const { t } = useT();
  const m = t.moeglichkeitenNach;
  const sectionRef = useRef(null);
  const [step, setStep] = useState(0);
  const stepRef = useRef(0);
  const lockRef = useRef(0);

  const icons = {
    deliver: (
      <Truck aria-hidden strokeWidth={1.4} className="w-16 h-16 mn-icon" style={{ animationDelay: '0s' }} />
    ),
    sellToUs: (
      <img
        src="/source-pptx/image57.svg"
        alt=""
        aria-hidden
        className="w-16 h-16 select-none mn-icon mn-icon--img"
        draggable={false}
      />
    ),
    sellThirdParty: (
      <HandCoins aria-hidden strokeWidth={1.4} className="w-16 h-16 mn-icon" style={{ animationDelay: '0.4s' }} />
    ),
    offer: (
      <BellRing aria-hidden strokeWidth={1.4} className="w-16 h-16 mn-icon" style={{ animationDelay: '0.8s' }} />
    ),
  };

  const visibleOptions = (m.options || []).filter((opt) => opt.key !== 'sellToUs');
  const totalSteps = visibleOptions.length; // 3 options

  const advance = useCallback(() => {
    const now = Date.now();
    if (now - lockRef.current < 260) return; // dedupe rapid double-clicks
    lockRef.current = now;
    stepRef.current += 1;
    setStep(stepRef.current);
  }, []);

  // Global click listener (capture phase) that (a) fires BEFORE
  // SmoothScroll's window handler, (b) only reacts when the click
  // originated inside this section, (c) reveals the next card and
  // swallows the event UNTIL all cards are visible.
  useEffect(() => {
    const sec = sectionRef.current?.closest('section[data-section]');
    if (!sec) return undefined;

    const centred = () => {
      const r = sec.getBoundingClientRect();
      const vh = window.innerHeight;
      return r.top < vh * 0.65 && r.bottom > vh * 0.35;
    };

    const clickInsideSection = (e) => {
      const path = typeof e.composedPath === 'function' ? e.composedPath() : [];
      return path.some((n) => n === sec);
    };

    const isInteractive = (e) => {
      const path = typeof e.composedPath === 'function' ? e.composedPath() : [];
      return path.some((n) => {
        if (!n || n.nodeType !== 1) return false;
        return typeof n.matches === 'function'
          && n.matches('a, input, textarea, select, label, .lang-switcher, [data-testid^="language-"], header');
      });
    };

    const onClick = (e) => {
      if (!centred()) return;
      if (!clickInsideSection(e)) return;
      if (isInteractive(e)) return;
      if (stepRef.current < totalSteps) {
        e.stopPropagation();
        e.stopImmediatePropagation();
        advance();
      }
      // else → do nothing, let SmoothScroll advance to the next slide.
    };

    const onKey = (e) => {
      if (!centred()) return;
      if (![' ', 'Enter', 'ArrowRight', 'PageDown', 'ArrowDown'].includes(e.key)) return;
      if (stepRef.current < totalSteps) {
        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();
        advance();
      }
    };

    window.addEventListener('click', onClick, { capture: true });
    window.addEventListener('keydown', onKey, { capture: true });
    return () => {
      window.removeEventListener('click', onClick, { capture: true });
      window.removeEventListener('keydown', onKey, { capture: true });
    };
  }, [advance, totalSteps]);

  // Reset the step counter whenever the slide leaves the viewport so
  // the animation replays on the presenter's next visit.
  useEffect(() => {
    const sec = sectionRef.current?.closest('section[data-section]');
    if (!sec) return undefined;
    const io = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting || entry.intersectionRatio < 0.25) {
          stepRef.current = 0;
          setStep(0);
        }
      },
      { threshold: [0, 0.25, 0.6] },
    );
    io.observe(sec);
    return () => io.disconnect();
  }, []);

  // Only give the section a "block advance" hint while we still have
  // cards to reveal. Once all cards are shown, SmoothScroll should
  // handle the advance naturally on the next click.
  const noAdvance = step < totalSteps;

  return (
    <SectionShell
      id="moeglichkeiten-nach"
      centerContent
      className=""
      style={{ cursor: 'pointer' }}
      {...(noAdvance ? { 'data-no-advance': true } : {})}
    >
      <div ref={sectionRef} className="w-full" data-testid="mn-inner">
        {/* Headline + intro text — always visible */}
        <Reveal>
          <div className="overline mb-4">{m.overline}</div>
          <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl tracking-tight leading-[1.1] text-white mb-4 max-w-none">
            {m.title}
          </h2>
          <p className="font-ui text-3xl lg:text-4xl text-white/80 leading-relaxed max-w-none mb-12">
            {m.sub}
          </p>
        </Reveal>

        {/* Options — revealed one-by-one via clicks (data-visible controls fade-in) */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-10 md:gap-12">
          {visibleOptions.map((opt, i) => {
            const shown = step > i;
            return (
              <div
                key={opt.key}
                data-testid={`moeglichkeit-${opt.key}`}
                className="h-full border border-white/10 rounded-sm bg-white/[0.02] p-8 flex flex-col items-start gap-6 mn-option-card"
                data-visible={shown ? 'true' : 'false'}
                style={{
                  opacity: shown ? 1 : 0,
                  transform: shown ? 'translateY(0)' : 'translateY(28px)',
                  transition: 'opacity 520ms ease, transform 520ms ease',
                  pointerEvents: shown ? 'auto' : 'none',
                }}
              >
                <div className="flex items-center justify-center w-16 h-16 [&>*]:w-14 [&>*]:h-14">
                  {icons[opt.key]}
                </div>
                <div>
                  <div className="font-display text-3xl md:text-4xl font-semibold text-[#f2d979] tracking-tight mb-4">
                    {opt.title}
                  </div>
                  <p className="font-ui text-xl md:text-2xl text-white/75 leading-relaxed">
                    {opt.body}
                  </p>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </SectionShell>
  );
}
